import React, { useState, createContext, useContext } from 'react';
import { PaymentMethod } from './OrdersContext';
export type Currency = 'ETB' | 'USD' | 'EUR' | 'GBP';
export type Language = 'en' | 'am' | 'or';
export type MenuItemImagePolicy = 'per-item' | 'default' | 'none';
export type GeneralSettings = {
  businessName: string;
  tagline: string;
  description: string;
  logoUrl: string;
  coverUrl: string;
  currency: Currency;
  language: Language;
  timezone: string;
  email: string;
  phone: string;
  website: string;
  instagram: string;
  facebook: string;
  defaultBranchId?: string;
  menuItemImagePolicy: MenuItemImagePolicy;
  defaultMenuItemImage: string;
  commissionRates: Record<PaymentMethod, number>;
};
const initial: GeneralSettings = {
  businessName: 'Lion QR',
  tagline: 'Authentic Ethiopian Cuisine & Coffee',
  description: 'Experience the rich flavors of Ethiopia right in the heart of the city. We serve traditional injera dishes, premium roasted coffee, and modern fusion plates.',
  logoUrl: '',
  coverUrl: 'https://images.unsplash.com/photo-1551632436-cbf8dd35adfa?auto=format&fit=crop&q=80&w=2000&h=600',
  currency: 'ETB',
  language: 'en',
  timezone: 'Africa/Addis_Ababa',
  email: 'hello@lionqr.com',
  phone: '+251 911 123 456',
  website: 'https://lionqr.com',
  instagram: '@lionqr',
  facebook: 'LionQR',
  defaultBranchId: undefined,
  menuItemImagePolicy: 'per-item',
  defaultMenuItemImage: '',
  commissionRates: {
    telebirr: 0.75,
    'cbe-birr': 0.75,
    'bank-transfer': 0.75,
    card: 2.9,
    cash: 0
  }
};
type Ctx = {
  general: GeneralSettings;
  updateGeneral: (patch: Partial<GeneralSettings>) => void;
};
const SettingsContext = createContext<Ctx | null>(null);
export function SettingsProvider({
  children
}: {
  children: ReactNode;
}) {
  const [general, setGeneral] = useState<GeneralSettings>(initial);
  const updateGeneral = (patch: Partial<GeneralSettings>) => {
    setGeneral(prev => ({
      ...prev,
      ...patch
    }));
  };
  return <SettingsContext.Provider value={{
    general,
    updateGeneral
  }} data-id="element-3415">
      {children}
    </SettingsContext.Provider>;
}
export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
export const currencyMeta: Record<Currency, {
  label: string;
  symbol: string;
}> = {
  ETB: {
    label: 'Ethiopian Birr (ETB)',
    symbol: 'Br'
  },
  USD: {
    label: 'US Dollar (USD)',
    symbol: '$'
  },
  EUR: {
    label: 'Euro (EUR)',
    symbol: '€'
  },
  GBP: {
    label: 'British Pound (GBP)',
    symbol: '£'
  }
};
export const languageMeta: Record<Language, {
  label: string;
  native: string;
}> = {
  en: {
    label: 'English',
    native: 'English'
  },
  am: {
    label: 'Amharic',
    native: 'አማርኛ'
  },
  or: {
    label: 'Afaan Oromo',
    native: 'Afaan Oromoo'
  }
};
export const timezoneOptions = [{
  value: 'Africa/Addis_Ababa',
  label: 'Addis Ababa (EAT, GMT+3)'
}, {
  value: 'Africa/Nairobi',
  label: 'Nairobi (EAT, GMT+3)'
}, {
  value: 'Africa/Cairo',
  label: 'Cairo (EET, GMT+2)'
}, {
  value: 'Africa/Lagos',
  label: 'Lagos (WAT, GMT+1)'
}, {
  value: 'Europe/London',
  label: 'London (GMT, GMT+0)'
}, {
  value: 'Europe/Paris',
  label: 'Paris (CET, GMT+1)'
}, {
  value: 'America/New_York',
  label: 'New York (EST, GMT-5)'
}, {
  value: 'Asia/Dubai',
  label: 'Dubai (GST, GMT+4)'
}];